import { useOutletContext } from 'react-router';
import FileExplorer from '@/components/FileExplorer/FileExplorer';
import { styled } from '@mui/material/styles';
import ResourcesPageHeader from '@/components/ResourcesPageHeader/ResourcesPageHeader';
import {
  fetchMarketingAssets,
  fetchMarketingAssetsByFolder,
  fetchMarketingAssetsFolderStructure,
  fetchUnifiedSearch,
} from '@/lib/api';
import { useDownload } from '@/hooks/useDownload';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  SortByOptions,
  mapFiltersToParams,
  parseSearchHitPath,
} from './ResourcePageHelpers';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import {
  FolderStructureItem,
  FolderStructureResponse,
  MarketingAssetSchema,
  WithFormatted,
  UnifiedSearchResponse,
} from '@/lib/types';
import { useFileExplorerData } from '@/hooks/useFileExplorerData';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import { FormattedMessage, useIntl } from 'react-intl';
import ResourcesSearchPropositions from '@/components/ResourcesPageHeader/ResourcesSearchPropositions';
import { useScrollContainer } from '@/contexts/ScrollContainerContext';
import Button from '@/components/Button/Button';
import DownloadIcon from '@/assets/download.svg?react';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import { useBasketContext } from '@/contexts/BasketContext';
import BasketBar from '@/components/ShareBasketModal/BasketBar';
import ShareBasketModal from '@/components/ShareBasketModal/ShareBasketModal';
import ModeSwitch from '@/components/FileExplorer/ModeSwitch';
import { ga } from '@/lib/analytics';
import { SortOrder } from '@/components/SortControl/SortControl';

const MARKETING_HIDDEN_ROOT = 'marketing_assets';
const EMPTY_FOLDER_STRUCTURE: FolderStructureResponse = {
  folders: [
    {
      path: MARKETING_HIDDEN_ROOT,
      name: '',
      file_count: 0,
      total_file_count: 0,
      subfolders: [],
    },
  ],
  total_files: 0,
};

const MarketingAssetsResourcesPage = () => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState('');
  const [committedSearchText, setCommittedSearchText] = useState('');
  const [sortBy, setSortBy] = useState<string>(
    SortByOptions[0].value,
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const { idsFor, addMany, removeMany } = useBasketContext();
  const [shareOpen, setShareOpen] = useState(false);
  const selected = useMemo(
    () => idsFor('marketing_assets'),
    [idsFor],
  );
  const [focusedAssetId, setFocusedAssetId] = useState<number | null>(
    null,
  );
  const [path, setPath] = useState<string[]>([]);
  const [gridMode, setGridMode] = useState<
    'explorer' | 'search' | 'proposition'
  >('explorer');
  const [currentView, setCurrentView] = useState<Mode>('grid');
  const [propositionAsset, setPropositionAsset] =
    useState<WithFormatted<MarketingAssetSchema> | null>(null);

  const trimmedCommittedSearchText = committedSearchText.trim();
  const hasSufficientCommittedSearchText =
    trimmedCommittedSearchText.length >= 2;
  const filters: FilterSchema = useOutletContext();
  const filterParams = mapFiltersToParams(filters, sortBy, {
    sort_order: sortOrder,
  });
  const scrollContainerRef = useScrollContainer();
  const searchPropositionsPopperRef = useRef<HTMLDivElement>(null);

  const trimmedSearchText = searchText.trim();
  const hasSufficientSearchText = trimmedSearchText.length >= 2;

  const {
    data: searchSuggestions,
    isLoading: isSearchSuggestionsLoading,
  } = useInfiniteScroll<
    WithFormatted<MarketingAssetSchema>,
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Marketing Assets Resources Search Propositions',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedSearchText,
        ttype: ['marketing_assets'],
        ...filterParams,
        limit,
        offset,
      }),
    queryKey: [
      ...assetQueryKeys.marketing_assets.unifiedSearchPropositions,
      trimmedSearchText,
      ...Object.values(filterParams),
    ],
    scrollContainerRef: searchPropositionsPopperRef,
    useWindowScroll: false,
    pageSize: 5,
    scrollThreshold: 80,
    enabled: hasSufficientSearchText,
    extractData: (response) =>
      (response.marketing_assets
        ?.hits as WithFormatted<MarketingAssetSchema>[]) ?? [],
  });

  const {
    folderStructure,
    filesFlatData,
    loading,
    loadingNextPage,
    totalCount,
  } = useFileExplorerData({
    filters,
    sortBy,
    sortOrder,
    path,
    enabled: gridMode === 'explorer',
    scrollContainerRef: scrollContainerRef || undefined,
    folderStructureFn: fetchMarketingAssetsFolderStructure,
    folderStructureQueryKey:
      assetQueryKeys.marketing_assets.folderStructure,
    fetchFilesByFolderFn: fetchMarketingAssetsByFolder,
    fetchFilesByFolderQueryKey:
      assetQueryKeys.marketing_assets.byFolder,
    fetchFilesFlatFn: fetchMarketingAssets,
    fetchFilesFlatQueryKey: assetQueryKeys.marketing_assets.flat,
  });

  const { download, loading: downloadLoading } = useDownload();

  const hasSearchText = hasSufficientSearchText;

  const hasActiveSearchOrFilter =
    (searchText && hasSearchText) ||
    Object.values(filters).length > 0;

  const extractMarketingSearchHits = useCallback(
    (response: UnifiedSearchResponse) => {
      const hits =
        (response.marketing_assets
          ?.hits as WithFormatted<MarketingAssetSchema>[]) ?? [];
      return hits.map((asset) => {
        if (!asset.path) return asset;
        const parsedPath = parseSearchHitPath(
          asset.path,
          MARKETING_HIDDEN_ROOT,
        );
        if (parsedPath == null) return asset;
        return {
          ...asset,
          path: parsedPath,
        };
      });
    },
    [],
  );

  const clearSearchState = useCallback(() => {
    setSearchText('');
    setCommittedSearchText('');
    setGridMode('explorer');
    setPropositionAsset(null);
    setFocusedAssetId(null);
    setPath([MARKETING_HIDDEN_ROOT]);
  }, []);

  const handlePathChange = useCallback((nextPath: string[]) => {
    setPath(nextPath);
    setGridMode('explorer');
    setCommittedSearchText('');
    setPropositionAsset(null);
    setFocusedAssetId(null);
  }, []);

  const {
    data: searchResultsFlat,
    isLoading: isSearchResultsLoading,
    isFetchingNextPage: isSearchResultsFetchingNextPage,
    isFetching: isSearchResultsFetching,
    total_count: searchResultsTotalCount,
  } = useInfiniteScroll<
    WithFormatted<MarketingAssetSchema>,
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Marketing Assets Resources Search',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedCommittedSearchText,
        ttype: ['marketing_assets'],
        ...filterParams,
        limit,
        offset,
      }),
    queryKey: [
      ...assetQueryKeys.marketing_assets.resourcesSearch,
      trimmedCommittedSearchText,
      ...Object.values(filterParams),
    ],
    scrollContainerRef: scrollContainerRef || undefined,
    useWindowScroll: false,
    pageSize: 20,
    scrollThreshold: 200,
    enabled:
      gridMode === 'search' && hasSufficientCommittedSearchText,
    extractData: extractMarketingSearchHits,
  });

  // Reported once the results of an accepted search are in, so that
  // `result_count` can be sent along with the search term.
  const searchAcceptSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (gridMode !== 'search' || !hasSufficientCommittedSearchText) {
      searchAcceptSentRef.current = null;
      return;
    }
    if (isSearchResultsLoading) return;
    if (searchAcceptSentRef.current === trimmedCommittedSearchText)
      return;

    searchAcceptSentRef.current = trimmedCommittedSearchText;
    ga.trackSearchAccept({
      search_term: trimmedCommittedSearchText,
      result_count:
        searchResultsTotalCount ?? searchResultsFlat.length,
    });
  }, [
    gridMode,
    hasSufficientCommittedSearchText,
    isSearchResultsLoading,
    searchResultsFlat.length,
    searchResultsTotalCount,
    trimmedCommittedSearchText,
  ]);

  const propositionFolderStructure = useMemo(() => {
    if (gridMode !== 'proposition') return undefined;
    if (path.length === 0) return undefined;

    const createFolderItem = (idx: number): FolderStructureItem => {
      const isLeaf = idx === path.length - 1;
      return {
        path: path.slice(0, idx + 1).join('/'),
        name: path[idx],
        file_count: 0,
        total_file_count: 0,
        subfolders: isLeaf ? [] : [createFolderItem(idx + 1)],
      };
    };

    return {
      folders: [
        {
          path: path[0],
          name: '',
          file_count: 0,
          total_file_count: 0,
          subfolders: path.length > 1 ? [createFolderItem(1)] : [],
        },
      ],
      total_files: 0,
    };
  }, [gridMode, path]);

  const explorerFiles: MarketingAssetSchema[] = useMemo(() => {
    if (gridMode === 'proposition') {
      return propositionAsset ? [propositionAsset] : [];
    }

    const baseFiles = (
      gridMode === 'search' ? searchResultsFlat : filesFlatData
    ) as MarketingAssetSchema[];

    if (focusedAssetId == null) return baseFiles;
    return baseFiles.filter((asset) => asset.id === focusedAssetId);
  }, [
    focusedAssetId,
    filesFlatData,
    gridMode,
    propositionAsset,
    searchResultsFlat,
  ]);

  // FileExplorer hands back the full next selection for this category;
  // reconcile it against the basket rather than replacing the basket, which
  // also holds the other category's picks.
  const handleSelect = useCallback(
    (next: number[]) => {
      const nextSet = new Set(next);
      if (next.length > 0) {
        ga.trackAssetMultiSelect({ assets_count: next.length });
      }
      addMany('marketing_assets', next);
      removeMany(
        'marketing_assets',
        idsFor('marketing_assets').filter((id) => !nextSet.has(id)),
      );
    },
    [addMany, removeMany, idsFor],
  );

  const loadedIds = useMemo(
    () => explorerFiles.map((file) => file.id),
    [explorerFiles],
  );
  const loadedTotal =
    gridMode === 'search' ? searchResultsTotalCount : totalCount;

  // The basket spans sections; this page's Download must only ever act on
  // rows actually on screen, so scope it to what is loaded here.
  const viewSelected = useMemo(
    () => loadedIds.filter((id) => selected.includes(id)),
    [loadedIds, selected],
  );

  const handleDownloadClick = useMemo(() => {
    return () => {
      if (!viewSelected.length) return;

      const downloadParams: {
        category: 'marketing_assets';
        file_ids: number[];
        extension?: string;
        entity_name?: string;
      } = {
        category: 'marketing_assets',
        file_ids: viewSelected,
      };

      if (viewSelected.length === 1) {
        const allFilesForNaming = (
          gridMode === 'proposition'
            ? propositionAsset
              ? ([propositionAsset] as MarketingAssetSchema[])
              : []
            : gridMode === 'search'
              ? ((searchResultsFlat as MarketingAssetSchema[]) ?? [])
              : ((filesFlatData as MarketingAssetSchema[]) ?? [])
        ) as MarketingAssetSchema[];

        const selectedFile = allFilesForNaming.find(
          (file) => file.id === viewSelected[0],
        );
        if (selectedFile?.extension) {
          downloadParams.extension = selectedFile.extension;
        }
        if (selectedFile?.name) {
          downloadParams.entity_name = selectedFile.name;
        }
      }

      ga.trackAssetBulkDownload({
        assets_count: viewSelected.length,
      });
      download(downloadParams);
    };
  }, [
    gridMode,
    download,
    filesFlatData,
    propositionAsset,
    searchResultsFlat,
    viewSelected,
  ]);

  return (
    <Root className="wide-scrollbar">
      <ResourcesPageHeader
        title={
          <FormattedMessage
            id="searchWithinMarketingAssets"
            defaultMessage="Search within Marketing Assets"
          />
        }
        searchPlaceholder={intl.formatMessage({
          id: 'searchMarketingAssetsPlaceholder',
          defaultMessage: 'Search by name, dimension or extension',
        })}
        sortBy={sortBy}
        searchText={searchText}
        onSortByChange={setSortBy}
        onSearchTextChange={setSearchText}
        options={SortByOptions}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        loading={isSearchSuggestionsLoading}
        searchPropositionsPopperRef={searchPropositionsPopperRef}
        onSearchAccept={() => {
          if (trimmedSearchText.length >= 2) {
            setCommittedSearchText(trimmedSearchText);
            setGridMode('search');
            setPropositionAsset(null);
            setFocusedAssetId(null);
            setPath([MARKETING_HIDDEN_ROOT]);
          } else {
            setCommittedSearchText('');
            setGridMode('explorer');
            setPropositionAsset(null);
            setFocusedAssetId(null);
          }
        }}
        onSearchCancel={clearSearchState}
        actions={
          <>
            <Button
              variant="contained"
              onClick={handleDownloadClick}
              disabled={!viewSelected.length}
              aria-label="Download selected"
              loading={downloadLoading}
            >
              <DownloadIcon />
              <FormattedMessage
                id="downloadWithCount"
                defaultMessage="Download{suffix}"
                values={{
                  suffix: viewSelected.length
                    ? ` (${viewSelected.length})`
                    : '',
                }}
              />
            </Button>
            <ModeSwitch
              mode={currentView}
              onChange={setCurrentView}
            />
          </>
        }
        propositions={
          hasSearchText && searchSuggestions.length ? (
            <ResourcesSearchPropositions
              variant="marketing"
              marketing_assets={searchSuggestions}
              onMarketingClick={(asset) => {
                if (!asset.path) return;

                const pathParts = asset.path.split('/');
                if (pathParts.length <= 1) return;

                const normalizedPathParts = [...pathParts];
                const folderPathParts = [...pathParts];
                folderPathParts.pop();

                setPropositionAsset({
                  ...asset,
                  path: normalizedPathParts.join('/'),
                });
                setGridMode('proposition');
                setCommittedSearchText('');
                setPath(folderPathParts);
                setFocusedAssetId(asset.id);
                addMany('marketing_assets', [asset.id]);
              }}
            />
          ) : null
        }
      />
      <ExplorerWrapper>
        <FileExplorer
          folderStructure={
            gridMode === 'search'
              ? EMPTY_FOLDER_STRUCTURE
              : gridMode === 'proposition'
                ? propositionFolderStructure
                : folderStructure
          }
          files={explorerFiles}
          filesFlatList={
            gridMode === 'explorer'
              ? (filesFlatData as MarketingAssetSchema[] | undefined)
              : undefined
          }
          loading={
            gridMode === 'search'
              ? isSearchResultsLoading || isSearchResultsFetching
              : gridMode === 'explorer'
                ? loading
                : false
          }
          selected={selected}
          onSelect={handleSelect}
          showDownload={false}
          showSelectedLabel={false}
          path={path}
          onPathChange={handlePathChange}
          hiddenRoot={MARKETING_HIDDEN_ROOT}
          currentView={currentView}
          showHeader={false}
          type="marketing"
          downloadLoading={downloadLoading}
          loadingNextPage={
            gridMode === 'search'
              ? isSearchResultsFetchingNextPage
              : gridMode === 'explorer'
                ? loadingNextPage
                : false
          }
          hasActiveSearchOrFilter={hasActiveSearchOrFilter}
          onAllFilesClick={clearSearchState}
        />
      </ExplorerWrapper>
      <BasketBar
        variant="sticky"
        category="marketing_assets"
        loadedIds={loadedIds}
        totalCount={loadedTotal}
        onShareClick={() => setShareOpen(true)}
      />
      <ShareBasketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  padding: `0 ${theme.spacing(4)}`,
  // Fill the scrollport and lay the page out as a column, so the basket bar
  // below can be pushed to the bottom edge.
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
}));

// The file list used to be separated from the header by the explorer's own
// header row, which this page no longer renders.
const ExplorerWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  // Takes the slack on a short page so the basket bar lands at the bottom;
  flex: '1 0 auto',
}));

export default MarketingAssetsResourcesPage;
